import { config } from 'shared/config';
import type { Interaction, QueryWindow, Result, Results, VisualDefinition } from 'features/html-reporting/public/types';
import type { FilterValue, ListedFilter } from 'features/filters/public/model/types';
import type { TableSort } from 'features/filters/public/useFilterPane';

/** A dataset as an anonymous viewer is told about it: its paging, never a query. */
export type PublicDataset = { id: string; title: string; paging: boolean; unavailable?: boolean };
/** A filter the page's visitors see (the filter pane): never a hidden one or its field's id. `target` is a visual filter's visualUid. */
export type PublicFilter = Pick<ListedFilter, 'filterUid' | 'scope' | 'target' | 'label' | 'field' | 'type' | 'kind' | 'default' | 'locked'>;
export type PublicPageDocument = {
    kind?: 'html'; name: string; version: number; html: string; visuals: VisualDefinition[]; datasets: PublicDataset[];
    dataContext: string; dataRevision: string; filters?: PublicFilter[];
};

type Appearance = Record<string, any>;
export type LinkVisual = {
    visualUid: string; type: string; title?: string | null; position: Record<string, number | null>; appearance: Appearance; unavailable?: true;
    rendererSpec?: any; subtitle?: string | null; columns?: unknown[]; matrixSpec?: any; shapeType?: string | null;
};
export type LinkPage = {
    kind: 'native'; name: string; version: number; dataContext: string; dataRevision: string;
    canvas: { width: number; height: number; backgroundColor?: string | null; outerBackgroundColor?: string | null }; visuals: LinkVisual[];
    filters?: PublicFilter[];
};

export type PublicData = { version: number; context: string; results: Results };

/** What the viewer shows for a link that cannot serve: the backend's generic code, never its text. */
export const GONE_CODES: readonly string[] = ['link_not_found', 'link_revoked', 'link_expired', 'link_unavailable'];

export class PublicLinkError extends Error {
    constructor(readonly status: number, readonly code: string, message: string, readonly retryAfter?: number) {
        super(message);
    }
    get gone() { return GONE_CODES.includes(this.code); }
    get busy() { return this.code === 'rate_limited'; }
}

/**
 * Every viewer request is anonymous: no cookie, no Authorization header, no referrer. The token is a path segment of
 * this URL. A read that carries the visit's state sends it as a JSON `body` (a POST), so no URL holds it.
 */
export async function publicRequest<T>(token: string, path: string, signal?: AbortSignal, body?: object): Promise<T> {
    let response: Response;
    try {
        response = await fetch(`${config.API_URL}/api/public/pages/${encodeURIComponent(token)}${path}`, {
            credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', redirect: 'error', signal,
            ...(body ? { method: 'POST', body: JSON.stringify(body), headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
                : { headers: { Accept: 'application/json' } }),
        });
    } catch (error) {
        if (signal?.aborted) throw error;
        throw new PublicLinkError(0, 'network', 'The connection was interrupted.');
    }
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const header = Number(response.headers.get('Retry-After'));
        const code = typeof body?.code === 'string' ? body.code : 'unavailable';
        // An address the server does not know (a malformed token) reads like any unknown link.
        throw new PublicLinkError(response.status, response.status === 404 && !GONE_CODES.includes(code) ? 'link_not_found' : code,
            typeof body?.error === 'string' ? body.error : 'This page could not be loaded.', Number.isFinite(header) && header > 0 ? header : undefined);
    }
    return response.json();
}

export async function readAsset(token: string, id: string, signal?: AbortSignal): Promise<Blob> {
    let response: Response;
    try {
        response = await fetch(`${config.API_URL}/api/public/pages/${encodeURIComponent(token)}/assets/${encodeURIComponent(id)}`,
            { credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', redirect: 'error', signal });
    } catch (error) {
        if (signal?.aborted) throw error;
        throw new PublicLinkError(0, 'network', 'The connection was interrupted.');
    }
    if (!response.ok) throw new PublicLinkError(response.status, 'asset_unavailable', 'An image could not be loaded.');
    return response.blob();
}

/** The page of either kind: an HTML page's markup, or a native page's canvas and visuals. */
export const readPage = (token: string, signal?: AbortSignal) => publicRequest<PublicPageDocument | LinkPage>(token, '/page', signal);

/** `version` moves with every change of the page, a move or resize included.
 * `dataRevision` moves with the report's model, which changes what the page shows without touching the page. A filter edit reaches an open
 * visit on its next load. */
export const readVersion = (token: string, signal?: AbortSignal) =>
    publicRequest<{ version: number; dataRevision: string }>(token, '/version', signal);

/** The visit's own view (decision 20): its filter values (`{filterUid: value}`, null shows all), sent with every read of the link, and
 * its table sorts (`{visualUid: sort}`), sent with that table's reads. Never stored, lost on reload. The visit's filter pane sets the
 * values and Reset clears both (`useVisitFilters`); a table's header sets its sort. */
type Visit = { token: string; values: Record<string, FilterValue>; sorts: Record<string, TableSort> };
let visit: Visit = { token: '', values: {}, sorts: {} };
const visitListeners = new Set<() => void>();
const changeVisit = (next: Visit) => { visit = next; visitListeners.forEach(listener => listener()); };
/** For `useSyncExternalStore`: the visit now, and a subscription to its changes. */
export const currentVisit = () => visit;
export const subscribeVisit = (listener: () => void) => { visitListeners.add(listener); return () => { visitListeners.delete(listener); }; };
const visitOf = (token: string): Visit => visit.token === token ? visit : { token, values: {}, sorts: {} };
export const visitValues = (token: string) => visitOf(token).values;
export const setVisitValues = (token: string, values: Record<string, FilterValue>) => changeVisit({ ...visitOf(token), values });
export const visitSort = (token: string, visualUid: string): TableSort | null => visitOf(token).sorts[visualUid] ?? null;
export const setVisitSort = (token: string, visualUid: string, sort: TableSort | null) => {
    const sorts = { ...visitOf(token).sorts };
    if (sort) sorts[visualUid] = sort;
    else delete sorts[visualUid];
    changeVisit({ ...visitOf(token), sorts });
};
export const resetVisit = (token: string) => changeVisit({ token, values: {}, sorts: {} });
/** A read refused the visit's values (`filter_refused`: a filter removed, locked or hidden since the page was read; `state_too_large`,
 * or `request_too_large` past the transport limit): the pane drops them and reads the page again. The event's detail is the server's
 * sentence. */
export const VISIT_REFUSED_EVENT = 'unity:public-visit-refused';
const VISIT_REFUSALS = ['filter_refused', 'state_too_large', 'request_too_large'];
const sent = (token: string) => Object.keys(visitValues(token)).length ? visit.values : undefined;
/** Runs a read that carried `values`; a refusal of them is told once (later reads that carried the same values say nothing). */
async function carrying<T>(values: Record<string, FilterValue> | undefined, run: () => Promise<T>): Promise<T> {
    try {
        return await run();
    } catch (error) {
        if (values && values === visit.values && error instanceof PublicLinkError && VISIT_REFUSALS.includes(error.code)) {
            window.dispatchEvent(new CustomEvent(VISIT_REFUSED_EVENT, { detail: error.message }));
        }
        throw error;
    }
}

/** An HTML page sends the `interaction` of its script; a native page the `interactionFilters` its canvas builds for a click. The visit's
 * filter values go with every read. */
type DataState = { interaction?: Interaction | null; interactionFilters?: unknown[]; window?: QueryWindow; targets?: string[]; view?: unknown;
    filterValues?: Record<string, FilterValue> };
export const readData = (token: string, state: DataState, target?: string, signal?: AbortSignal) => {
    const clean: DataState = {};
    const values = sent(token);
    if (values) clean.filterValues = values;
    if (state.interaction) clean.interaction = state.interaction;
    if (state.interactionFilters?.length) clean.interactionFilters = state.interactionFilters;
    if (state.window) {
        // A viewer's window is capped (200 rows) and has no seek token; the server refuses more.
        const { pageIndex, pageSize, sorting, search } = state.window;
        clean.window = { pageIndex, pageSize: Math.min(pageSize ?? 200, 200), sorting, search };
    }
    if (state.targets?.length) clean.targets = state.targets;
    if (state.view) clean.view = state.view;
    return carrying(values, () => publicRequest<PublicData>(token, '/data', signal, { state: clean, ...(target ? { target } : {}) }));
};

/** One page of a visible filter's members under the visit's filters (`POST /filter-values`), never a hidden or locked filter's. */
export const readFilterMembers = (token: string, filterUid: string, search: string, offset: number, signal?: AbortSignal) => {
    const values = sent(token);
    return carrying(values, () => publicRequest<{ values: unknown[]; hasMore: boolean; nextOffset: number | null }>(
        token, '/filter-values', signal, { filterUid, offset, ...(search ? { q: search } : {}), state: values ? { filterValues: values } : {} }));
};

/** The wait before trying a throttled request again: the server's `Retry-After`, growing with each attempt, capped at 30 s. */
export const backoffSeconds = (error: PublicLinkError, attempt: number) =>
    Math.min(30, Math.max(error.retryAfter ?? 2, 2) * Math.pow(1.5, attempt));

/** A visual saved as SQL (code legacy_sql) reads this one sentence for every reader: a state, not a failure. */
export const LEGACY_SQL = 'This visual was saved as SQL, which visuals no longer use; rebuild it.';

const friendly: Record<string, string> = {
    legacy_sql: LEGACY_SQL,
    source_unavailable: 'This data can’t be shown on a public link',
    too_slow: 'Took too long to load',
    data_unavailable: 'This data isn’t available right now',
    rate_limited: 'Too many people are viewing this right now',
};
/** Words the page can show where a dataset failed: the code decides, the server's wording only for the row limit. */
export const friendlyError = (code: string | undefined, fallback?: string) =>
    friendly[code ?? ''] ?? (code === 'snapshot_row_limit' && fallback ? fallback : friendly.data_unavailable);

/** A failed slot carries the friendly sentence as its error so the page shows it where the data would have been. */
export const presentResults = (results: Results): Results => Object.fromEntries(Object.entries(results).map(([key, result]) =>
    [key, (result as Result & { code?: string })?.error ? { ...result, error: friendlyError((result as Result & { code?: string }).code, result.error) } : result]));

/** Wait `ms`, or reject with an AbortError when `signal` stops. */
export const sleep = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
    const timer = window.setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, ms);
    const stop = () => { window.clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
    signal.addEventListener('abort', stop, { once: true });
});

/** Run a request, waiting out the server's throttling (`rate_limited`) with a growing delay. `onWait` hears the wait in seconds, then null. */
export async function retrying<T>(run: () => Promise<T>, signal: AbortSignal, onWait?: (seconds: number | null) => void): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        try {
            const value = await run();
            onWait?.(null);
            return value;
        } catch (error) {
            if (!(error instanceof PublicLinkError) || !error.busy || attempt >= 5) { onWait?.(null); throw error; }
            const seconds = backoffSeconds(error, attempt);
            onWait?.(Math.ceil(seconds));
            await sleep(seconds * 1000, signal);
        }
    }
}

